// Internal
import { ENVIRONMENT } from "src/constants/settings";
import { store } from "src/redux/store";
import { sendLog } from "src/services/businessAnalyticsLogServices";
import { resolveStackLines } from "src/helpers/sourceMapResolver";

// Reports frontend crashes to /api/businessanalyticsservice/log so developers
// see them before users escalate.

type TErrorSource = "error_boundary" | "window_error" | "unhandled_rejection";

const MAX_VALUE_LENGTH = 4000;
const MAX_LINES = 20;
// Same error within this window is reported once (e.g. a crashing list row).
const DEDUPE_WINDOW_MS = 10_000;
// Hard cap per page load so a runaway loop can't flood the API.
const MAX_LOGS_PER_SESSION = 100;

const IGNORED_MESSAGES = [
  "ResizeObserver loop",
  // Cross-origin scripts hide the real error; nothing actionable.
  "Script error.",
];

const lastSentAt = new Map<string, number>();
let sentCount = 0;

const truncate = (value?: string | null) =>
  value ? value?.slice(0, MAX_VALUE_LENGTH) : "";

// "http://localhost:3000/static/js/src_pages_X_tsx.chunk.js" -> "src_pages_X_tsx.chunk.js"
const stripOrigin = (line: string) =>
  line?.replace(/https?:\/\/[^/\s)]+\/(static\/js\/)?/g, "");

// Source-maps each frame back to the original file (e.g. src/pages/X/index.tsx:124:1)
// before stripping the origin from any frame that couldn't be mapped.
const toLines = async (value?: string | null) => {
  // A thrown non-Error object can carry a non-string `stack`.
  if (typeof value !== "string" || !value) return [];
  const lines = value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, MAX_LINES);
  const resolved = await resolveStackLines(lines);
  return resolved.map(stripOrigin);
};

// Stack frame lines end in ":line:col" (optionally followed by ")").
const isFrame = (line: string) => /:\d+:\d+\)?$/?.test(line);

// Source-mapped frames from our own code point into "src/"; libraries into "node_modules/".
const isAppFrame = (line: string) =>
  /\bsrc\//.test(line) && !line.includes("node_modules");

// Function/component name of a frame, if it has one.
// Chrome: "at Name (file:1:2)"  |  Firefox: "Name@file:1:2"
const frameName = (line: string) =>
  line?.match(/^at\s+([^\s(]+)\s+\(/)?.[1] ?? line?.match(/^([^@\s]+)@/)?.[1];

// Prefer the first frame in our own code — the top frame is often an anonymous
// callback (e.g. a useEffect) while the first *named* frame is React internals.
// Falls back to the first named frame when source maps aren't available.
const pickLocation = (lines: string[]) => {
  const frames = lines?.filter(isFrame);
  return (
    frames?.find(isAppFrame) ??
    frames?.find((line) => frameName(line)) ??
    frames[0] ??
    ""
  );
};

const normalizeError = (reason: unknown): Error => {
  if (reason instanceof Error) return reason;
  if (typeof reason === "string") return new Error(reason);
  try {
    return new Error(JSON.stringify(reason));
  } catch {
    return new Error(String(reason));
  }
};

const shouldSend = (key: string, message: string) => {
  if (sentCount >= MAX_LOGS_PER_SESSION) return false;
  if (IGNORED_MESSAGES?.some((ignored) => message?.includes(ignored))) {
    return false;
  }

  const now = Date.now();
  const last = lastSentAt?.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;

  lastSentAt?.set(key, now);
  sentCount += 1;
  return true;
};

const getUserContext = () => {
  const credential: any = store.getState()?.credential;
  return {
    isLoggedIn: Boolean(credential?.userCred?.accessToken),
    orgId: localStorage.getItem("company_id") ?? "",
    userEmail: credential?.userTokenDecode?.email ?? "",
  };
};

export const logFrontendError = async (
  reason: unknown,
  source: TErrorSource,
  componentStack?: string | null,
  isWarning?: boolean,
) => {
  // Logging must never throw — this runs inside error handlers.
  try {
    const error = normalizeError(reason);
    const message = error?.message || error.toString();
    const [stackLines, componentLines] = await Promise.all([
      toLines(error?.stack),
      toLines(componentStack),
    ]);
    const location = pickLocation(stackLines);
    // Nearest React component that was rendering (error boundary only).
    const componentFrame = componentLines?.find(isFrame);
    const component = componentFrame ? frameName(componentFrame) ?? "" : "";

    if (!shouldSend(`${source}|${message}|${location}`, message)) return;

    const { isLoggedIn, orgId, userEmail } = getUserContext();

    await sendLog(
      {
        logType: isWarning ? "warning" : "error",
        parameters: {
          orgId,
          userEmail,
          url: window.location.pathname,
          fullUrl: truncate(window.location.href),
          message: truncate(message),
          errorName: error?.name,
          errorSource: source,
          errorLocation: truncate(location),
          errorComponent: component,
          errorStack: truncate(stackLines?.join("\n")),
          componentStack: truncate(componentLines?.join("\n")),
          environment: ENVIRONMENT ?? "",
          userAgent: truncate(navigator?.userAgent),
        },
      },
      isLoggedIn,
    );
  } catch {
    // swallow
  }
};

let globalHandlersRegistered = false;

// ErrorBoundary only catches render errors; these catch event-handler,
// async and unhandled-promise errors too.
export const registerGlobalErrorLogging = () => {
  if (globalHandlersRegistered) return;
  globalHandlersRegistered = true;

  window.addEventListener("error", (event) => {
    logFrontendError(event.error ?? event.message, "window_error", null, true);
  });

  window.addEventListener("unhandledrejection", (event) => {
    logFrontendError(event.reason, "unhandled_rejection", null, true);
  });
};
